import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import moment from 'moment';
import UserSearch from 'components/UserSearch';
import theme, { Theme } from 'helpers/theme';
import PageHeader from 'components/PageHeader';
import Table from 'sharedComponents/core/Table';
import UserLabel from 'sharedComponents/core/Table/UserLabel';
import Text from 'sharedComponents/core/Table/Text/Text';
import CourseCompletion from 'sharedComponents/core/CourseCompletion';
import Icon from 'sharedComponents/core/Icon';
import Dropdown, { DropdownOption } from 'sharedComponents/core/Input/Dropdown';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import Paginator, { Page } from 'sharedComponents/Pagination/Paginator';
import { createFragmentContainer, graphql } from 'react-relay';
import { Router, useRouter } from 'found';
import {
  DelegatesPage_delegates,
  CourseStatus
} from './__generated__/DelegatesPage_delegates.graphql';
import { DelegatesPage_manager } from './__generated__/DelegatesPage_manager.graphql';
import { fetchQuery } from 'relay-runtime';
import environment from 'api/environment';
import { DelegatesPageQueryResponse } from './__generated__/DelegatesPageQuery.graphql';
import Button from 'components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpWideShort,
  faBarsFilter
} from '@fortawesome/pro-solid-svg-icons';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import DelegatesFilterOptions from 'components/Delegate/DelegatesFilterOptions';
import DelegatesSortOptions from 'components/Delegate/DelegatesSortOptions';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '50px 60px'
  },
  searchAndFilter: {
    display: 'flex',
    justifyContent: 'space-between',
    height: 40
  },
  tableOptions: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dropdown: {
    background: 'white'
  },
  dropdownOption: {
    display: 'flex',
    background: 'white',
    width: '200px'
  },
  divider: {
    width: theme.spacing(1)
  },
  search: {
    flex: 0.4
  },
  actionsRow: {
    borderLeft: '1px solid #ededed',
    paddingLeft: 23,
    height: 38,
    display: 'flex',
    /* justify-content: center; */
    alignItems: 'center'
  },
  checkBox: {
    marginLeft: 0,
    marginRight: '10px',
    cursor: 'pointer',
    transform: 'scale(1.2)'
  }
}));

const delegateRow = (
  userUUID: string,
  name: string,
  profileUrl: string | undefined,
  email: string,
  coursesCompleted: number,
  totalCourses: number,
  lastActiveTimestamp: string,
  nextExpiryTimestamp: string,
  classes: any,
  router: any
): any => {
  return {
    key: userUUID,
    cells: [
      { component: () => <UserLabel name={name} profileUrl={profileUrl} /> },
      {
        component: () => <Text text={email} color={theme.colors.textBlue} />
      },
      {
        component: () => (
          <CourseCompletion total={totalCourses} complete={coursesCompleted} />
        )
      },
      { component: () => <Text text={lastActiveTimestamp} formatDate /> },
      { component: () => <Text text={nextExpiryTimestamp} formatDate /> }
      // {
      //   component: () => (
      //     <div className={classes.actionsRow}>
      //       <Icon name={'Card_SecondaryActon_Dots'} />
      //     </div>
      //   )
      // }
    ],
    onClick: () => router.push(`/app/delegates/${userUUID}`)
  };
};

type Props = {
  delegates: DelegatesPage_delegates;
  manager: DelegatesPage_manager;
  router: Router;
};

const dropdownToSort = {
  0: { field: 'first_name', ascending: true },
  1: { field: 'first_name', ascending: false },
  2: { field: 'expiration_date', ascending: true },
  3: { field: 'expiration_date', ascending: false }
};

export const ALL_COMPLETED = 'allCompleted';
export const EXPIRING_BEFORE_6 = 'expiringBefore6';
export const EXPIRING_BEFORE_3 = 'expiringBefore3';
export const HAS_INCOMPLETE_COURSE = 'incompleteCourse';
export const HAS_ACTIVE_DOCUMENT = 'hasActiveDocument';
export const HAS_EXPIRED_DOCUMENT = 'hasExpiredDocument';
export const HAS_ACTIVE_BACKGROUND_CHECK = 'hasActiveBackgroundCheck';
export const HAS_EXPIRED_BACKGROUND_CHECK = 'hasExpiredBackgroundCheck';

const DelegatesPage = ({ delegates, manager }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [sortOption, setSortOption] = React.useState<
    DropdownOption | undefined
  >(undefined);

  const action = useActionMenuDispatch();
  const { router, match } = useRouter();

  const rows = delegates.edges ?? [];
  const pageInfo = {
    total: delegates.pageInfo?.total ?? 0,
    limit: delegates.pageInfo?.limit ?? 10,
    offset: delegates.pageInfo?.offset ?? 0
  };
  const page = {
    currentPage: Math.ceil(pageInfo.offset / pageInfo.limit) + 1,
    totalPages: Math.ceil(pageInfo.total / pageInfo.limit)
  };

  const delegateComponents = rows.map((delegate: any) =>
    delegateRow(
      delegate?.uuid,
      `${delegate?.firstName} ${delegate?.lastName}`,
      '',
      delegate?.email,
      ([...delegate.myCourses, ...delegate.myClassroomCourses] as Array<{
        status: CourseStatus;
      }>).filter((course) => course.status !== 'incomplete').length,
      delegate.myCourses.length + delegate.myClassroomCourses.length,
      delegate?.lastLogin,
      delegate?.nextExpiry,
      classes,
      router
    )
  );

  const currentField = !!match.location?.query?.field
    ? '&field=' + match.location?.query?.field
    : '';

  const currentAscending = !!match.location?.query?.ascending
    ? '&ascending=' + match.location?.query?.ascending
    : '';

  const currentFilter = !!match.location?.query?.filter
    ? '&filter=' + match.location?.query?.filter
    : '';

  const currentSecondaryCat = !!match.location?.query?.secondaryCategory
    ? '&secondaryCategory=' + match.location?.query?.secondaryCategory
    : '';

  const currentHasActiveCert = !!match.location?.query?.hasActiveCert
    ? '&hasActiveCert=' + match.location?.query?.hasActiveCert
    : '';

  const currentHasExpiredCert = !!match.location?.query?.hasExpiredCert
    ? '&hasExpiredCert=' + match.location?.query?.hasExpiredCert
    : '';

  const simulatorCompliant = !!match.location?.query?.simulatorCompliant
    ? '&simulatorCompliant=' + match.location?.query?.simulatorCompliant
    : '';

  const onUpdatePage = ({ offset, limit }: Page) => {
    if (offset <= 0) {
      offset = 1;
    }

    router.push(
      `/app/delegates?offset=${
        (offset - 1) * limit
      }&limit=${limit}${currentField}${currentAscending}${currentFilter}${currentSecondaryCat}${currentHasActiveCert}${currentHasExpiredCert}${simulatorCompliant}`
    );
  };

  const onFilterClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <DelegatesFilterOptions
          state={{
            allComplete: match.location?.query?.filter === ALL_COMPLETED,
            sixMonths: match.location?.query?.filter === EXPIRING_BEFORE_6,
            threeMonths: match.location?.query?.filter === EXPIRING_BEFORE_3,
            incompleteCourse:
              match.location?.query?.filter === HAS_INCOMPLETE_COURSE,
            hasActiveDocument:
              match.location?.query?.filter === HAS_ACTIVE_DOCUMENT,
            hasExpiredDocument:
              match.location?.query?.filter === HAS_EXPIRED_DOCUMENT,
            hasActiveBackgroundCheck:
              match.location?.query?.filter === HAS_ACTIVE_BACKGROUND_CHECK,
            hasExpiredBackgroundCheck:
              match.location?.query?.filter === HAS_EXPIRED_BACKGROUND_CHECK
          }}
          onChange={(newState) => {
            let filterField = '';

            if (newState.allComplete) {
              filterField = `&filter=${ALL_COMPLETED}`;
            } else if (newState.sixMonths) {
              filterField = `&filter=${EXPIRING_BEFORE_6}`;
            } else if (newState.threeMonths) {
              filterField = `&filter=${EXPIRING_BEFORE_3}`;
            } else if (newState.incompleteCourse) {
              filterField = `&filter=${HAS_INCOMPLETE_COURSE}`;
            } else if (newState.hasActiveDocument) {
              filterField = `&filter=${HAS_ACTIVE_DOCUMENT}`;
            } else if (newState.hasExpiredDocument) {
              filterField = `&filter=${HAS_EXPIRED_DOCUMENT}`;
            } else if (newState.hasActiveBackgroundCheck) {
              filterField = `&filter=${HAS_ACTIVE_BACKGROUND_CHECK}`;
            } else if (newState.hasExpiredBackgroundCheck) {
              filterField = `&filter=${HAS_EXPIRED_BACKGROUND_CHECK}`;
            }

            router.push(
              `${window.location.pathname}?limit=${
                match?.location?.query?.limit ?? 10
              }&offset=${
                match?.location?.query?.offset ?? 0
              }${currentAscending}${currentField}${filterField}`
            );
          }}
        />
      )
    });
  };

  const onSortClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <DelegatesSortOptions
          state={{
            nameASC:
              match.location?.query?.field === 'first_name' &&
              match.location?.query?.ascending === 'true',
            nameDESC:
              match.location?.query?.field === 'first_name' &&
              match.location?.query?.ascending === 'false',
            expiringASC:
              match.location?.query?.field === 'expiration_date' &&
              match.location?.query?.ascending === 'true',
            expiringDESC:
              match.location?.query?.field === 'expiration_date' &&
              match.location?.query?.ascending === 'false'
          }}
          onChange={(newState) => {
            let sortField = '';

            if (newState.nameASC) {
              sortField = `&field=first_name&ascending=true`;
            } else if (newState.nameDESC) {
              sortField = `&field=first_name&ascending=false`;
            } else if (newState.expiringASC) {
              sortField = `&field=expiration_date&ascending=true`;
            } else if (newState.expiringDESC) {
              sortField = `&field=expiration_date&ascending=false`;
            }

            router.push(
              `${window.location.pathname}?limit=${
                match?.location?.query?.limit ?? 10
              }&offset=${
                match?.location?.query?.offset ?? 0
              }${sortField}${currentFilter}`
            );
          }}
        />
      )
    });
  };

  return (
    <div className={classes.root}>
      <PageHeader
        title={manager.company.name}
        subTitle="Organisation Overview"
        sideText={`${pageInfo?.total} members total`}
      />
      <div className={classes.searchAndFilter}>
        <div className={classes.search}>
          <UserSearch
            companyName={manager.company.name}
            searchFunction={async (text: string) => {
              const query = graphql`
                query DelegatesPageQuery($name: String!) {
                  delegates(filter: { name: $name }, page: { limit: 8 }) {
                    edges {
                      uuid
                      TTC_ID
                      firstName
                      lastName
                    }
                  }
                }
              `;

              const variables = {
                name: text
              };

              const data = (await fetchQuery(
                environment,
                query,
                variables
              )) as DelegatesPageQueryResponse;

              if (!data || !data.delegates || !data.delegates.edges) {
                console.error('Could not get data', data);
                return [];
              }

              const results = data.delegates?.edges.map((delegate) => ({
                uuid: delegate?.uuid ?? '',
                key: `${delegate?.firstName} ${delegate?.lastName}`,
                value: delegate?.TTC_ID ?? ''
              }));

              return results;
            }}
          />
        </div>
        <div className={classes.tableOptions}>
          {(manager.company?.simulatorCompliance?.activeUsers ?? 0) > 0 && (
            <>
              <Button
                archetype="flat"
                onClick={() =>
                  window.open(
                    manager?.company?.simulatorReportPreviewURL ?? '',
                    '_blank'
                  )
                }
              >
                Download 6x6 report
              </Button>
              <Spacer spacing={2} horizontal />
            </>
          )}
          <Button
            archetype="flat"
            onClick={onFilterClick}
            active={!!match.location?.query?.filter}
          >
            <FontAwesomeIcon icon={faBarsFilter} /> Filter
          </Button>
          <Spacer spacing={1} horizontal />
          <Button
            archetype="flat"
            onClick={onSortClick}
            active={!!match.location.query.field}
          >
            <FontAwesomeIcon icon={faArrowUpWideShort} /> Sort by
          </Button>
        </div>
      </div>
      <Spacer spacing={3} vertical />
      <Table
        header={[
          'Name',
          'Email',
          'Courses Completed',
          'Last Active',
          'Next Expiry'
        ]}
        rows={delegateComponents}
      />
      <Spacer vertical spacing={3} />
      <Paginator
        currentPage={page.currentPage}
        updatePage={onUpdatePage}
        numPages={page.totalPages}
        itemsPerPage={pageInfo.limit}
        showRange={page.totalPages}
      />
    </div>
  );
};

const DelegatesPageFrag = createFragmentContainer(DelegatesPage, {
  delegates: graphql`
    fragment DelegatesPage_delegates on DelegatePage {
      edges {
        uuid
        email
        firstName
        lastName
        lastLogin
        createdAt
        myCourses {
          status
        }
        myClassroomCourses {
          status
        }
        nextExpiry
      }
      pageInfo {
        total
        offset
        limit
        given
      }
    }
  `,
  manager: graphql`
    fragment DelegatesPage_manager on Manager {
      company {
        name
        simulatorReportPreviewURL
        simulatorCompliance {
          activeUsers
        }
      }
    }
  `
});

export default DelegatesPageFrag;
